
import { Order } from '../../../types';
import { DistanceService } from '../core/distanceService';
import { calculateOrderSlack } from '../core/timing';
import { TORTUOSITY_FACTOR, COLD_START_MINS_PER_KM } from '../constants';

const getPointAngle = (lat: number, lng: number, hubLat: number, hubLng: number): number => {
    const dy = lat - hubLat;
    const dx = lng - hubLng;
    let theta = Math.atan2(dy, dx);
    theta *= 180 / Math.PI;
    if (theta < 0) theta += 360;
    return theta;
};

const getAngleDiff = (a: number, b: number): number => {
    let diff = Math.abs(a - b);
    return diff > 180 ? 360 - diff : diff;
};

/**
 * v45.4: S.A.L. CLUSTERING ENGINE - FULL PARAMETER INTEGRITY
 * Aplica rigorosamente os limites de Cone, Zig-Zag, Distância entre pedidos e Bag.
 */
export const formClusters = async (
    pool: Order[],
    profile: any,
    hubLat: number,
    hubLng: number,
    _googleMapsApiKey?: string,
    systemConfig?: any,
    mode: 'own' | 'third' = 'own'
): Promise<Order[][]> => {
    if (pool.length === 0) return [];

    // Extração Rígida de Parâmetros (Independentes por Perfil)
    const MAX_ORDERS = profile?.maxOrdersPerRoute || profile?.maxOrders || 4;
    const MAX_RADIUS = profile?.clusteringMaxRadiusKm || 3;
    const MAX_ANGLE = profile?.clusteringMaxAngleDegrees || 90;
    const ZIGZAG_DIST = profile?.clusteringMinDistanceForAngleCheck || 0.7;
    const ZIGZAG_TIME = profile?.zigzagIgnoreTimeMinutes || 5;

    // v56.3: Speed is now handled by DistanceService.getTravelTime (no local SPEED_FACTOR needed)
    // v50.0: Centralized Constants
    // const MINS_PER_KM = COLD_START_MINS_PER_KM; // REMOVED: Use DistanceService
    const BASE_SERVICE_TIME = profile?.serviceTimeMinutes || 5;

    const sortedBySlack = [...pool].sort((a, b) => {
        const slackA = systemConfig ? calculateOrderSlack(a, systemConfig) : 999;
        const slackB = systemConfig ? calculateOrderSlack(b, systemConfig) : 999;
        return slackA - slackB;
    });

    const clusters: Order[][] = [];
    const processedIds = new Set<string>();

    for (const anchor of sortedBySlack) {
        if (processedIds.has(anchor.id)) continue;

        const currentCluster: Order[] = [anchor];
        processedIds.add(anchor.id);

        if (!anchor.lat || !anchor.lng) {
            clusters.push(currentCluster);
            continue;
        }

        const anchorAngle = getPointAngle(anchor.lat, anchor.lng, hubLat, hubLng);
        // Pre-calcula Slack da Âncora para validação de integridade
        const anchorSlack = systemConfig ? calculateOrderSlack(anchor, systemConfig) : 999;
        const distHubToAnchor = DistanceService.getDistanceSync(hubLat, hubLng, anchor.lat, anchor.lng);

        const candidates = sortedBySlack.filter(o => !processedIds.has(o.id) && o.lat && o.lng);

        for (const candidate of candidates) {
            if (currentCluster.length >= MAX_ORDERS) break;

            // 1. Verificação de Distância entre Pedidos
            const distToAnchor = DistanceService.getDistanceSync(anchor.lat, anchor.lng, candidate.lat!, candidate.lng!);
            if (distToAnchor > MAX_RADIUS) continue;

            // 2. Verificação de Cone e Zig-Zag
            const distToHub = DistanceService.getDistanceSync(hubLat, hubLng, candidate.lat!, candidate.lng!);
            const candAngle = getPointAngle(candidate.lat!, candidate.lng!, hubLat, hubLng);
            const angleDiff = getAngleDiff(anchorAngle, candAngle);

            // Regra Zig-Zag: Se estiver perto (< ZIGZAG_DIST) ou for rápido (< ZIGZAG_TIME), ignora o cone
            // Caso contrário, respeita o limite de abertura da rota
            // v55.3: Usa distância corrigida (já com fator) para tempo de viagem
            // v56.0: Centralized Time Logic
            const travelTimeToMe = candidate.estimatedDuration && candidate.estimatedDuration > 0
                ? candidate.estimatedDuration
                : DistanceService.getTravelTime(distToHub, mode);
            const isZigZagExempt = distToAnchor < ZIGZAG_DIST || travelTimeToMe < ZIGZAG_TIME;

            if (!isZigZagExempt && angleDiff > (MAX_ANGLE / 2)) {
                continue;
            }

            // 3. Verificação de SLA-e (O pedido ainda cabe na janela de tempo?)
            // PROTEÇÃO DE INTEGRIDADE (Stress Test do Paciente Zero)
            // Simula o pior caso: Candidato inserido ANTES da âncora
            // v55.3: DetourKm agora é linear pois os termos já estão corrigidos pelo DistanceService
            const detourKm = (distToHub + distToAnchor) - distHubToAnchor;
            // v56.0: Centralized Time Logic
            const detourMins = DistanceService.getTravelTime(detourKm, mode);
            const impactMins = detourMins + BASE_SERVICE_TIME; // Impacto total na Âncora

            // Se o slack da âncora não suporta o impacto, rejeita o candidato para proteger o Paciente Zero
            // v56.2: "Crisis Mode" - Se a âncora JÁ está atrasada (slack < 0), 
            // agrupar é MELHOR do que criar duas viagens separadas para o mesmo motoboy.
            // A proteção só se aplica a ordens que AINDA podem ser salvas.
            if (anchorSlack >= 0 && (anchorSlack - impactMins) < 0) {
                continue;
            }

            // Validação do Próprio Candidato (já existente)
            const slack = systemConfig ? calculateOrderSlack(candidate, systemConfig) : 999;
            const tolerance = profile?.slaToleranceMinutes || 10;

            if (slack < (travelTimeToMe - tolerance)) {
                continue;
            }

            currentCluster.push(candidate);
            processedIds.add(candidate.id);
        }
        clusters.push(currentCluster);
    }

    return clusters;
}
